// California income-tax engine (Form 540 pipeline), a clean-room rebuild of Xspouse's ca_tax.dll.
// Pure functions, no I/O. All amounts are ANNUAL dollars. See notes/ca_tax.md for the recovered algorithm.
import type { CaTaxInput, CaTaxResult, IrsStatus, TaxYear } from '@divorceos/schema';
import y2022 from '../data/2022.json';
import y2023 from '../data/2023.json';
import y2024 from '../data/2024.json';
import y2025 from '../data/2025.json';
import y2026 from '../data/2026.json';
import y2027 from '../data/2027.json';

export interface BracketRow { over: number; rate: number }

type ByStatus<T> = Record<IrsStatus, T>;
type ByWhStatus<T> = Record<'SINGLE' | 'MFJ' | 'HOH', T>;

/** Shape of packages/tax-ca/data/<year>.json. */
export interface CaYearData {
  year: number;
  source: string;
  verified: boolean;
  notes: string[];
  brackets: ByStatus<BracketRow[]>;
  standardDeduction: ByStatus<number>;
  exemptionCredit: {
    personal: number; dependent: number;
    phaseOutStep: number; phaseOutStepMfs: number; reductionPerStep: number;
    agiThreshold: ByStatus<number>;
  };
  itemizedLimitation: { maxReductionPct: number; pctOfAgiExcess: number; miscFloorPct: number; charitableLimitPct: number };
  renterCredit: { agiLimit: ByStatus<number>; amount: ByStatus<number> };
  jchhCredit: { rate: number; cap: number; minTimeshare: number; maxTimeshare: number };
  childCareCredit: {
    expenseCapPerPerson: number; maxPersons: number; fedStartPct: number; fedMinPct: number;
    fedAgiFloor: number; fedAgiStep: number; caPctByFedAgi: { upTo: number; pct: number }[];
  };
  calEitc: {
    maxIncome: number; investmentIncomeLimit: number; minEarnedIncome: number;
    plateauStart: number[]; maxCredit: number[]; phaseInRate: number[]; phaseOutRate: number[]; lineSlope: number[];
    /** Exact FTB 3514 lookup table: [earnedIncomeHigh, 0 children, 1, 2, 3+]. Rows are $50 wide; lower bound = previous high + 1. */
    table?: number[][];
  };
  yctc: { amount: number; phaseOutStart: number; phaseOutEnd: number };
  fytc: { amount: number; phaseOutStart: number; phaseOutEnd: number };
  amt: {
    rate: number; exemptionPhaseOutRate: number; medicalAdjPct: number;
    exemption: ByStatus<number>; phaseOutStart: ByStatus<number>; phaseOutEnd: ByStatus<number>;
  };
  mentalHealthTax: { threshold: number; rate: number };
  sdi: { rate: number; wageCap: number | null };
  withholding: {
    source: string; rates: number[];
    standardDeduction: ByWhStatus<number>; lowIncomeExemption: ByWhStatus<number>; creditPerAllowance: number;
    brackets: ByWhStatus<BracketRow[]>;
  };
}

const DATA: Record<TaxYear, CaYearData> = {
  2022: y2022 as unknown as CaYearData,
  2023: y2023 as unknown as CaYearData,
  2024: y2024 as unknown as CaYearData,
  2025: y2025 as unknown as CaYearData,
  2026: y2026 as unknown as CaYearData,
  2027: y2027 as unknown as CaYearData,
};

/**
 * Which figures to use: 'ftb' = the published FTB tables in data/<year>.json; 'xspouse' = the tables the
 * original ca_tax.dll builds (notes/ca_tax.md §2, §4): 2022 constants x 1.069 (2023) / x 1.069 x 1.041 (2024),
 * the 2022 CalEITC table unindexed, YCTC/FYTC 1,083 with an indexed phase-out slope, CalEITC investment-income
 * limit 4,389 x factor, SDI 2024 = 1.1 % capped at 153,164 x 1.041, DE 44 2024 = 2023 x 1.041.
 * The original's 2023/2024 figures are NOT the legal ones; 'xspouse' exists to reproduce it (compat mode).
 */
export type CaTables = 'ftb' | 'xspouse';

/** ca_tax.dll FUN_100039d5: cumulative index factor per year (idx 24..26 only have bracket tables). */
const XSPOUSE_FACTOR: Partial<Record<TaxYear, number>> = { 2022: 1, 2023: 1.069, 2024: 1.069 * 1.041 };
/** ca_tax.dll FUN_1000e800 SDI table (2024+: 1.1 % on 153,164 x FUN_10001099(idx)). */
const XSPOUSE_SDI: Partial<Record<TaxYear, { rate: number; wageCap: number }>> = {
  2022: { rate: 0.011, wageCap: 145600 }, 2023: { rate: 0.009, wageCap: 153164 }, 2024: { rate: 0.011, wageCap: 153164 * 1.041 },
};
const XSPOUSE_CACHE: Partial<Record<TaxYear, CaYearData>> = {};

const mapStatus = <T,>(o: ByStatus<T>, f: (v: T, k: IrsStatus) => T): ByStatus<T> =>
  ({ SINGLE: f(o.SINGLE, 'SINGLE'), MFS: f(o.MFS, 'MFS'), HOH: f(o.HOH, 'HOH'), MFJ: f(o.MFJ, 'MFJ') });

/** The tables ca_tax.dll computes for `year`: the 2022 base constants scaled by the DLL's index factor. */
function xspouseYearData(year: TaxYear): CaYearData {
  const cached = XSPOUSE_CACHE[year];
  if (cached) return cached;
  const f = XSPOUSE_FACTOR[year];
  if (f === undefined) throw new Error(`tax-ca: the original engine has no tables for tax year ${year}`);
  const b = DATA[2022];
  const wh = year === 2024 ? DATA[2023].withholding : DATA[year].withholding; // explicit DE 44 for 2022/2023
  const whScale = year === 2024 ? 1.041 : 1; // FUN_10001099(26) — not cumulative; the $1,000,000 step is not scaled
  const scaleRows = (rows: BracketRow[], k: number) => rows.map((r) => ({ over: r.over === 1000000 && k !== f ? r.over : r.over * k, rate: r.rate }));
  const d: CaYearData = {
    ...b,
    year,
    source: `ca_tax.dll (2022 constants x ${f.toFixed(6)}; notes/ca_tax.md §2/§4)`,
    verified: true,
    notes: [`Reproduces the original engine's ${year} figures (2022 base x ${f}); not the FTB-published ${year} values.`],
    brackets: mapStatus(b.brackets, (rows) => scaleRows(rows, f)),
    standardDeduction: mapStatus(b.standardDeduction, (v) => v * f),
    exemptionCredit: {
      ...b.exemptionCredit,
      personal: 140 * f, dependent: 433 * f,
      agiThreshold: mapStatus(b.exemptionCredit.agiThreshold, (v) => v * f),
    },
    renterCredit: { agiLimit: mapStatus(b.renterCredit.agiLimit, (v) => v * f), amount: { ...b.renterCredit.amount } },
    jchhCredit: { ...b.jchhCredit, cap: b.jchhCredit.cap * f },
    calEitc: {
      ...b.calEitc,
      investmentIncomeLimit: 4389 * f,
      plateauStart: b.calEitc.plateauStart.map((v) => v * f),
    },
    // 1,083 flat; the reduction per $100 over $25,000 is 21.66 x factor, i.e. the phase-out ends at 25,000 + 5,000 / f.
    yctc: { amount: 1083, phaseOutStart: 25000, phaseOutEnd: 25000 + 5000 / f },
    fytc: { amount: 1083, phaseOutStart: 25000, phaseOutEnd: 25000 + 5000 / f },
    amt: {
      ...b.amt,
      exemption: mapStatus(b.amt.exemption, (v) => v * f),
      phaseOutStart: mapStatus(b.amt.phaseOutStart, (v) => v * f),
      phaseOutEnd: mapStatus(b.amt.phaseOutEnd, (v) => v * f),
    },
    sdi: XSPOUSE_SDI[year] ?? b.sdi,
    withholding: {
      ...wh,
      standardDeduction: { SINGLE: wh.standardDeduction.SINGLE * whScale, MFJ: wh.standardDeduction.MFJ * whScale, HOH: wh.standardDeduction.HOH * whScale },
      lowIncomeExemption: { SINGLE: wh.lowIncomeExemption.SINGLE * whScale, MFJ: wh.lowIncomeExemption.MFJ * whScale, HOH: wh.lowIncomeExemption.HOH * whScale },
      creditPerAllowance: wh.creditPerAllowance * whScale,
      brackets: {
        SINGLE: whScale === 1 ? wh.brackets.SINGLE : scaleRows(wh.brackets.SINGLE, whScale),
        MFJ: whScale === 1 ? wh.brackets.MFJ : scaleRows(wh.brackets.MFJ, whScale),
        HOH: whScale === 1 ? wh.brackets.HOH : scaleRows(wh.brackets.HOH, whScale),
      },
    },
  };
  XSPOUSE_CACHE[year] = d;
  return d;
}

/** The data table for a year (exported for the UI's rate-table views). */
export function caYearData(year: TaxYear, tables: CaTables = 'ftb'): CaYearData {
  if (tables === 'xspouse') return xspouseYearData(year);
  const d = DATA[year];
  if (!d) throw new Error(`tax-ca: no data for tax year ${year}`);
  return d;
}

export interface CaTaxOptions { tables?: CaTables }

/** California income-tax rate schedule for a year / status (MFS uses the Single schedule). */
export function caBrackets(year: TaxYear, status: IrsStatus, tables: CaTables = 'ftb'): BracketRow[] {
  return caYearData(year, tables).brackets[status].map((r) => ({ over: r.over, rate: r.rate }));
}

/** Tax from a rate schedule plus the marginal (bracket) rate at `income`. */
export function bracketTax(rows: BracketRow[], income: number): { tax: number; rate: number } {
  let tax = 0;
  let rate = rows.length && income > 0 ? rows[0].rate : 0; // FUN_1001112b: no tax and no rate below $0.10
  for (let i = 0; i < rows.length; i++) {
    const lo = rows[i].over;
    if (income <= lo) break;
    const hi = i + 1 < rows.length ? rows[i + 1].over : Infinity;
    tax += (Math.min(income, hi) - lo) * rows[i].rate;
    rate = rows[i].rate;
  }
  return { tax, rate };
}

const max0 = (x: number): number => (x > 0 ? x : 0);

/** FTB rounding used by the refundable credits: >1 -> nearest dollar, (0,1] -> $1, <=0 -> 0. */
function creditRound(x: number): number {
  if (x <= 0) return 0;
  if (x <= 1) return 1;
  return Math.floor(x + 0.5);
}

/** CalEITC amount for an income figure and number of qualifying children (clamped to 3). */
export function calEitcLookup(year: TaxYear, income: number, qualifyingChildren: number, tables: CaTables = 'ftb'): number {
  const p = caYearData(year, tables).calEitc;
  const n = Math.max(0, Math.min(3, Math.floor(qualifyingChildren)));
  if (income < p.minEarnedIncome || income > p.maxIncome) return 0;
  if (p.table) {
    for (const row of p.table) if (income <= row[0]) return row[n + 1];
    return 0;
  }
  let c: number;
  if (income < p.plateauStart[n]) c = p.phaseInRate[n] * income;
  else c = Math.max(p.maxCredit[n] - p.phaseOutRate[n] * (income - p.plateauStart[n]), p.lineSlope[n] * (p.maxIncome - income));
  return creditRound(Math.min(c, p.maxCredit[n]));
}

/** YCTC / FYTC phase-out: full amount up to phaseOutStart, then linear to 0 at phaseOutEnd (2022: $21.66 per $100 over $25,000). */
function youngChildStyleCredit(p: { amount: number; phaseOutStart: number; phaseOutEnd: number }, earned: number, count: number): number {
  const full = p.amount * count;
  if (earned <= p.phaseOutStart) return full;
  const perDollar = p.amount / (p.phaseOutEnd - p.phaseOutStart);
  return creditRound(full - perDollar * (earned - p.phaseOutStart) * count);
}

/** Annual CA withholding, DE 44 Method B (exact calculation method). MFS uses the Single table. */
export function caWithholding(year: TaxYear, status: IrsStatus, annualWages: number, allowances: number, opts: CaTaxOptions = {}): number {
  const w = caYearData(year, opts.tables ?? 'ftb').withholding;
  const key = status === 'MFS' ? 'SINGLE' : status;
  let std = w.standardDeduction[key];
  let low = w.lowIncomeExemption[key];
  if (status === 'MFJ' && allowances > 1) { std *= 2; low *= 2; }
  if (annualWages <= low) return 0;
  const taxable = max0(annualWages - std);
  const tax = bracketTax(w.brackets[key], taxable).tax - allowances * w.creditPerAllowance;
  return max0(tax);
}

/** Compute the full California return (Form 540, Schedules CA and P, FTB 3506/3514, SDI). */
export function computeCaTax(input: CaTaxInput, opts: CaTaxOptions = {}): CaTaxResult {
  const tables = opts.tables ?? 'ftb';
  const d = caYearData(input.year, tables);
  const st = input.status;
  const lines: Record<string, number> = {};
  const fedAgi = input.federalAgi;

  // ---- Form 540 lines 7-11: exemption credits before phase-out --------------------------------
  const nPersonal = input.personalExemptions + input.age65Count + input.blindCount;
  const personalCredit = nPersonal * d.exemptionCredit.personal;
  const dependentCredit = input.dependents * d.exemptionCredit.dependent;
  lines['540.7'] = input.personalExemptions * d.exemptionCredit.personal;
  lines['540.8'] = input.blindCount * d.exemptionCredit.personal;
  lines['540.9'] = input.age65Count * d.exemptionCredit.personal;
  lines['540.10'] = dependentCredit;
  lines['540.11'] = personalCredit + dependentCredit;

  // ---- Schedule CA Part I: federal AGI -> CA AGI ---------------------------------------------
  lines['540.13'] = fedAgi;
  let subtractions = input.unemployment + input.federalTaxableSocialSecurity + input.subtractFromAgi + input.alimonyPaidCaDeductible;
  let additions = input.addToAgi + input.alimonyReceivedCaTaxable;
  if (subtractions < 0) { additions -= subtractions; subtractions = 0; }
  if (additions < 0) { subtractions -= additions; additions = 0; }
  const caAgi = fedAgi - subtractions + additions;
  lines['SchCA.7.B'] = input.unemployment;
  lines['SchCA.6.B'] = input.federalTaxableSocialSecurity;
  lines['SchCA.19a.C'] = input.alimonyPaidCaDeductible;
  lines['SchCA.2a.C'] = input.alimonyReceivedCaTaxable;
  lines['SchCA.other.B'] = input.subtractFromAgi;
  lines['SchCA.other.C'] = input.addToAgi;
  lines['SchCA.27.B'] = subtractions;
  lines['SchCA.27.C'] = additions;
  lines['540.14'] = subtractions;
  lines['540.15'] = fedAgi - subtractions;
  lines['540.16'] = additions;
  lines['540.17'] = caAgi;

  // ---- Schedule CA Part II: CA itemized deductions vs standard deduction ----------------------
  const fi = input.federalItemized;
  const misc2 = max0(input.unionDues + input.jobExpensesAndMisc - d.itemizedLimitation.miscFloorPct * fedAgi);
  const itemizedBefore = fi.medicalAfterFloor + fi.realEstateTax + fi.mortgageInterest + fi.investmentInterest + fi.charitable + fi.misc + misc2;
  const notSubject = fi.medicalAfterFloor + fi.investmentInterest; // medical, investment interest (and casualty) are not limited
  const limThreshold = d.exemptionCredit.agiThreshold[st];
  let limitationReduction = 0;
  if (fedAgi > limThreshold) {
    limitationReduction = Math.min(
      d.itemizedLimitation.maxReductionPct * max0(itemizedBefore - notSubject),
      d.itemizedLimitation.pctOfAgiExcess * (fedAgi - limThreshold),
    );
  }
  const itemized = max0(itemizedBefore - limitationReduction);
  const standard = d.standardDeduction[st];
  const caItemizing = itemized > standard;
  const deduction = caItemizing ? itemized : standard;
  lines['SchCA.4'] = fi.medicalAfterFloor;
  lines['SchCA.5a.B'] = fi.stateIncomeTaxDeducted; // state income tax removed for CA
  lines['SchCA.5b'] = fi.realEstateTax;
  lines['SchCA.8'] = fi.mortgageInterest;
  lines['SchCA.9'] = fi.investmentInterest;
  lines['SchCA.14'] = fi.charitable;
  lines['SchCA.16'] = fi.misc;
  lines['SchCA.19'] = input.unionDues + input.jobExpensesAndMisc;
  lines['SchCA.21'] = d.itemizedLimitation.miscFloorPct * fedAgi;
  lines['SchCA.22'] = misc2;
  lines['SchCA.28'] = itemizedBefore;
  lines['SchCA.29.reduction'] = limitationReduction;
  lines['SchCA.29'] = itemized;
  lines['SchCA.30'] = standard;
  lines['540.18'] = deduction;

  // ---- Line 19 taxable income, line 31 tax --------------------------------------------------
  const taxableIncome = max0(caAgi - deduction);
  const { tax: taxBeforeCredits, rate: marginalRate } = bracketTax(d.brackets[st], taxableIncome);
  lines['540.19'] = taxableIncome;
  lines['540.31'] = taxBeforeCredits;

  // ---- Line 32 exemption credits with AGI phase-out (uses federal AGI, line 13) ---------------
  let exemptionCredit = 0;
  if (input.includeExemptionCredit) {
    const step = st === 'MFS' ? d.exemptionCredit.phaseOutStepMfs : d.exemptionCredit.phaseOutStep;
    const excess = max0(fedAgi - limThreshold);
    const steps = Math.ceil(excess / step);
    const reduction = steps * d.exemptionCredit.reductionPerStep;
    exemptionCredit = max0(personalCredit - reduction * nPersonal) + max0(dependentCredit - reduction * input.dependents);
    lines['540.32.excess'] = excess;
    lines['540.32.reduction'] = reduction;
  }
  lines['540.32'] = exemptionCredit;
  const line33 = max0(taxBeforeCredits - exemptionCredit);
  const line35 = line33; // line 34 (Schedule G-1 / FTB 5870A) not modelled
  lines['540.33'] = line33;
  lines['540.35'] = line35;

  // ---- Nonrefundable credits: child care (FTB 3506), JCHH, renter's ----------------------------
  let childCareCredit = 0;
  if (input.includeChildCareCredit && st !== 'MFS' && input.childCareExpenses > 0 && input.qualifyingChildrenCcc > 0) {
    const cc = d.childCareCredit;
    const capped = Math.min(input.childCareExpenses, cc.expenseCapPerPerson * Math.min(input.qualifyingChildrenCcc, cc.maxPersons));
    let least = Math.min(capped, input.earnedIncome);
    if (st === 'MFJ') least = Math.min(least, input.spouseEarnedIncome);
    least = max0(least);
    const fedSteps = Math.ceil(max0(fedAgi - cc.fedAgiFloor) / cc.fedAgiStep);
    const fedPct = Math.max(cc.fedMinPct, cc.fedStartPct - 0.01 * fedSteps);
    const fedCredit = least * fedPct;
    const bandRow = cc.caPctByFedAgi.find((b) => fedAgi <= b.upTo);
    const caPct = bandRow ? bandRow.pct : 0;
    childCareCredit = fedCredit * caPct;
    lines['3506.fedCredit'] = fedCredit;
    lines['3506.caPct'] = caPct;
  }
  lines['540.40'] = childCareCredit;

  let jchhCredit = 0;
  if (input.includeJchhCredit && (st === 'SINGLE' || st === 'MFS')) {
    const j = d.jchhCredit;
    if (input.maxChildTimeshare >= j.minTimeshare && input.maxChildTimeshare <= j.maxTimeshare) {
      jchhCredit = Math.min(j.rate * line35, j.cap);
    }
  }
  lines['540.43'] = jchhCredit;

  let rentCredit = 0;
  if (input.includeRentCredit && input.isRenter && caAgi <= d.renterCredit.agiLimit[st]) {
    rentCredit = d.renterCredit.amount[st];
  }
  lines['540.46'] = rentCredit;

  const totalCredits = childCareCredit + jchhCredit + rentCredit;
  lines['540.47'] = totalCredits;
  const taxAfterCredits = max0(line35 - totalCredits);
  lines['540.48'] = taxAfterCredits;

  // ---- Schedule P (540): alternative minimum tax --------------------------------------------
  const a = d.amt;
  let amtAdjustments: number;
  if (!caItemizing) {
    amtAdjustments = standard;
    lines['SchP.1'] = standard;
  } else {
    const medicalAdj = Math.min(a.medicalAdjPct * fedAgi, fi.medicalAfterFloor);
    lines['SchP.2'] = medicalAdj;
    lines['SchP.3'] = fi.realEstateTax;
    lines['SchP.5'] = misc2;
    amtAdjustments = medicalAdj + fi.realEstateTax + misc2;
  }
  lines['SchP.4'] = input.amtHomeMortgageInterest;
  lines['SchP.7'] = input.amtInvestmentInterest;
  lines['SchP.9'] = input.amtDepletion;
  lines['SchP.10'] = input.amtNol;
  lines['SchP.13'] = input.amtOtherAdjustments;
  lines['SchP.14'] = -limitationReduction;
  let amti = taxableIncome + amtAdjustments
    + input.amtHomeMortgageInterest + input.amtInvestmentInterest + input.amtDepletion + input.amtNol + input.amtOtherAdjustments
    - (caItemizing ? limitationReduction : 0);
  let amtExemption = a.exemption[st];
  const poStart = a.phaseOutStart[st];
  const poEnd = a.phaseOutEnd[st];
  if (st === 'MFS' && amti > poEnd) amti += Math.min(amtExemption, a.exemptionPhaseOutRate * (amti - poEnd));
  if (amti > poStart) amtExemption = amti < poEnd ? max0(amtExemption - a.exemptionPhaseOutRate * (amti - poStart)) : 0;
  const tentativeMinTax = max0(amti - amtExemption) * a.rate;
  const amt = max0(tentativeMinTax - taxBeforeCredits);
  lines['SchP.21'] = amti;
  lines['SchP.22'] = amtExemption;
  lines['SchP.23'] = max0(amti - amtExemption);
  lines['SchP.24'] = tentativeMinTax;
  lines['SchP.25'] = taxBeforeCredits;
  lines['SchP.26'] = amt;
  lines['540.61'] = amt;

  // ---- Line 62 mental health services tax, line 64 total tax ---------------------------------
  const mentalHealthTax = max0(taxableIncome - d.mentalHealthTax.threshold) * d.mentalHealthTax.rate;
  lines['540.62'] = mentalHealthTax;
  const totalTax = taxAfterCredits + amt + mentalHealthTax;
  lines['540.64'] = totalTax;

  // ---- Refundable credits: CalEITC (FTB 3514), YCTC, FYTC ------------------------------------
  const earned = input.earnedIncome + (st === 'MFJ' ? input.spouseEarnedIncome : 0);
  lines['3514.earnedIncome'] = earned;
  let caEitc = 0;
  const e = d.calEitc;
  if (st !== 'MFS' && input.investmentIncome <= e.investmentIncomeLimit && earned >= e.minEarnedIncome) {
    const nch = Math.max(0, Math.min(3, Math.floor(input.qualifyingChildrenEic)));
    caEitc = calEitcLookup(input.year, earned, nch, tables);
    if (fedAgi !== earned && fedAgi >= e.plateauStart[nch]) {
      caEitc = Math.min(caEitc, calEitcLookup(input.year, fedAgi, nch, tables));
    }
  }
  lines['540.75'] = caEitc;
  const yctc = input.hasYoungChild && caEitc > 0 ? youngChildStyleCredit(d.yctc, earned, 1) : 0;
  lines['540.76'] = yctc;
  const fytc = input.qualifiesFytc && caEitc > 0 ? youngChildStyleCredit(d.fytc, earned, 1) : 0;
  lines['540.77'] = fytc;
  lines['540.78'] = caEitc + yctc + fytc;

  // ---- ISR penalty (line 92), net --------------------------------------------------------------
  const isrPenalty = max0(input.isrPenalty);
  lines['540.92'] = isrPenalty;
  const netIncomeTax = totalTax + isrPenalty - (caEitc + yctc + fytc);
  lines['540.net'] = netIncomeTax;

  // ---- SDI (separate from the return) ----------------------------------------------------------
  let sdi = 0;
  if (input.includeSdi) {
    const base = d.sdi.wageCap == null ? input.sdiWages : Math.min(input.sdiWages, d.sdi.wageCap);
    sdi = max0(base) * d.sdi.rate;
  }
  lines['SDI'] = sdi;
  lines['540.marginalRate'] = marginalRate;

  const result: CaTaxResult = {
    caAdjustmentsSubtractions: subtractions,
    caAdjustmentsAdditions: additions,
    caAgi,
    caDeduction: deduction,
    caItemizing,
    caTaxableIncome: taxableIncome,
    taxBeforeCredits,
    exemptionCredit,
    jchhCredit,
    rentCredit,
    childCareCredit,
    taxAfterCredits,
    amt,
    mentalHealthTax,
    totalTax,
    caEitc,
    yctc,
    fytc,
    isrPenalty,
    netIncomeTax,
    sdi,
    marginalRate,
    lines,
  };

  if (!input.includeStateIncomeTax) {
    // State income tax switched off: the return is still computed for display, but nothing is owed / refunded.
    return { ...result, netIncomeTax: 0, lines: { ...lines, '540.net': 0 } };
  }
  return result;
}
